
import _ from "lodash";
import { 
  IFilter, 
  IFilterChips, 
  IFilterChipsEventData, 
  IRecordingLength, 
  ITranscriptSearchFilter,
  ITranscriptSearchFilterCondition,
  ITranscriptSearchFilterGroup,
  ShortCallsOptions, 
  TranscriptSearchFilterOptionTypes,
  IFilterViewModel } from "@/models/filter.model";
import { 
  ISelectOption, 
  ITreeItemFilter, 
  ObjectionCategory } from "@/models/shared.model";
import { 
  IScorecardFilter, 
  IScorecardFilterCondition, 
  ScorecardFilterConditionResults } from "@/models/scorecardFilter.model";
import { 
  FILTER_DIALOG_OPTIONS, 
  DEFAULT_SHORT_CALLS_OPTION, 
  DEFAULT_TRANSCRIPTION_SEARCH_OPTION, 
  DEFAULT_TRANSCRIPTION_SEARCH_VALUE, 
  SCORECARD_SCORED_OPTIONS,
  SHORT_CALLS_OPTIONS, 
  TRANSCRIPTION_SEARCH_CONJUNCTIONS, 
  TRANSCRIPTION_SEARCH_OPERATORS } from "@/constants/filter";

export const filterService = {
  getLabelForFilter: (filter: IFilter, filterData: IFilterViewModel | undefined): string | undefined => {
    if (!filter || !filterData) {
      return undefined;
    }

    let filterItemsLabel = undefined;
    for (const property in filter) {
      const propertyKey = property as keyof IFilter;
      const propertyValue = filter[propertyKey];
      let itemLabel = filterService.getLabelForFilterProperty(propertyKey, propertyValue, filterData);
      if (itemLabel) {
         filterItemsLabel = filterItemsLabel ? `${filterItemsLabel}; ${itemLabel}` : itemLabel;
      }
    }

    return filterItemsLabel;
  },

  getLabelForFilterProperty: (property: keyof IFilter, value: any, filterData: IFilterViewModel): string | undefined => {
    if ((!value && value !== false) || !filterData) {
      return undefined;
    }
  
    let filterItemLabel = undefined;

    if (property === FILTER_DIALOG_OPTIONS.recordingLength.name) {
      const selectedValue = value as IRecordingLength;
      if (!selectedValue || (selectedValue.min === filterData.minDuration && selectedValue.max === filterData.maxDuration)) {
        return undefined;
      }
      filterItemLabel = `${FILTER_DIALOG_OPTIONS.recordingLength.chipsLabel}: ${selectedValue.min.toString()} - ${selectedValue.max}`;
    }
    else if (property === FILTER_DIALOG_OPTIONS.isSuccess.name) {
      if (value) {
        filterItemLabel = `${FILTER_DIALOG_OPTIONS.isSuccess.chipsLabel}: ON`;
      }
    }
    else if (property === FILTER_DIALOG_OPTIONS.shortCalls.name) {
      const selectedValue = value as ShortCallsOptions;
      if (selectedValue === ShortCallsOptions.all) {
        return undefined;
      }
      const optionName = SHORT_CALLS_OPTIONS.find((item) => item.value === selectedValue)?.name ?? '';
      filterItemLabel = `${FILTER_DIALOG_OPTIONS.shortCalls.chipsLabel}: ${optionName}`;
    }
    else if (property === FILTER_DIALOG_OPTIONS.isScored.name) {
      const selectedValue = value as boolean;
      const optionName = SCORECARD_SCORED_OPTIONS.find((item) => item.value === selectedValue)?.name ?? '';
      filterItemLabel = `${FILTER_DIALOG_OPTIONS.isScored.chipsLabel}: ${optionName}`;
    }
    else { // filter dropdowns values
      if (Array.isArray(value) && value.length === 0) {
        return undefined;
      }
      const selectedValue = value as any[];
      const dataSourcePropertyName = FILTER_DIALOG_OPTIONS[property].dataSourceName as keyof IFilterViewModel;
      if (!dataSourcePropertyName) {
        return undefined;
      }
      const filterDataItems = filterData[dataSourcePropertyName] as ISelectOption<any>[];
      const selectedItems = filterDataItems.filter(dataItem => selectedValue.includes(dataItem.value));
      filterItemLabel = `${FILTER_DIALOG_OPTIONS[property].chipsLabel}: ${selectedItems?.length === 1 ? selectedItems[0].name : selectedItems.map(item => item.name).join(', ')}`;
    }
  
    return filterItemLabel;
  },

  getFilterRecordingLength: (value: IRecordingLength | undefined, minDuration: number | undefined, maxDuration: number | undefined): IRecordingLength => {
    return value ?? { min: minDuration, max: maxDuration} as IRecordingLength
  },

  isFilterRecordingLengthChanged: (value: IRecordingLength | undefined, minDuration: number | undefined, maxDuration: number | undefined): boolean => {
    if (!value) return false;
    const recordingLength = filterService.getFilterRecordingLength(value, minDuration, maxDuration);
    return recordingLength?.min !== minDuration || recordingLength.max !== maxDuration;
  },

  getChipsForRecordingLength(value: any) : IFilterChips | undefined {
    const selectedValue = value as IRecordingLength;
    if (!selectedValue) {
      return undefined;
    }

    return {
      property: FILTER_DIALOG_OPTIONS.recordingLength.name as keyof IFilter,
      label: `${FILTER_DIALOG_OPTIONS.recordingLength.chipsLabel}: ${selectedValue.min.toString()} - ${selectedValue.max}`,
      tooltipText: `${selectedValue.min.toString()} - ${selectedValue.max}`,
      showTooltip: false,
    };
  },

  getChipsForIsSuccess(value: any) : IFilterChips | undefined {
    if (!value) {
      return undefined;
    }

    const chipsLabel = `${FILTER_DIALOG_OPTIONS.isSuccess.chipsLabel}: ON`;
    return {
      property: FILTER_DIALOG_OPTIONS.isSuccess.name as keyof IFilter,
      label: chipsLabel,
      tooltipText: chipsLabel,
      showTooltip: false,
    };
  },

  getChipsForShortCalls(value: any) : IFilterChips | undefined {
    const selectedValue = value as ShortCallsOptions;
    if (selectedValue === ShortCallsOptions.all) {
      return undefined;
    }

    const optionName = SHORT_CALLS_OPTIONS.find((item) => item.value === selectedValue)?.name ?? '';
    if (!optionName) {
      return undefined;
    }

    return {
      property: FILTER_DIALOG_OPTIONS.shortCalls.name as keyof IFilter,
      label: `${FILTER_DIALOG_OPTIONS.shortCalls.chipsLabel}: ${optionName}`,
      tooltipText: optionName,
      showTooltip: false,
    };
  },

  getChipsForIsScored(value: any) : IFilterChips | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }

    const selectedValue = value as boolean;
    const optionName = SCORECARD_SCORED_OPTIONS.find((item) => item.value === selectedValue)?.name ?? '';
    if (!optionName) {
      return undefined;
    }

    return {
      property: FILTER_DIALOG_OPTIONS.isScored.name as keyof IFilter,
      label: `${FILTER_DIALOG_OPTIONS.isScored.chipsLabel}: ${optionName}`,
      tooltipText: optionName,
      showTooltip: false,
    };
  },

  getChipsForTranscriptionSearch(value: any) : IFilterChips[] | undefined {
    const filter = value as ITranscriptSearchFilter;
    if (!filter || !filter.options || !filter.options.length) {
      return undefined;
    }

    let chips: IFilterChips[] = [];

    filter.options.forEach((option, index) => {
      const isFirstChipsItem = chips.length === 0;
      if (option.type === TranscriptSearchFilterOptionTypes.condition) {
        const condition = option.value as ITranscriptSearchFilterCondition;
        if (condition.text) {
          const operatorName = TRANSCRIPTION_SEARCH_OPERATORS.find((operator) => operator.value === condition.operator)?.name;
          const conjunctionName = TRANSCRIPTION_SEARCH_CONJUNCTIONS.find((item) => item.value === filter.conjunction)?.name;
          const chipsLabel = `${isFirstChipsItem ? FILTER_DIALOG_OPTIONS.transcriptSearch.chipsLabel : conjunctionName} ${operatorName} '${condition.text}'`;
          const chipsTooltipText = isFirstChipsItem ? `${_.capitalize(operatorName)} '${condition.text}'` : `${conjunctionName} ${operatorName} '${condition.text}'`;
  
          chips.push({
            id: index.toString(),
            property: FILTER_DIALOG_OPTIONS.transcriptSearch.name as keyof IFilter,
            label: chipsLabel,
            tooltipText: chipsTooltipText,
            showTooltip: false,
          });
        }
      }
      else if (option.type === TranscriptSearchFilterOptionTypes.group) {
        const group = option.value as ITranscriptSearchFilterGroup;
        if (group.conditions.length && group.conditions.some(condition => condition.text)) {
          const conjunctionName = TRANSCRIPTION_SEARCH_CONJUNCTIONS.find((item) => item.value === filter.conjunction)?.name;
          const groupConjunctionName = TRANSCRIPTION_SEARCH_CONJUNCTIONS.find((item) => item.value === group.conjunction)?.name;

          let chipsLabel = `${isFirstChipsItem ? FILTER_DIALOG_OPTIONS.transcriptSearch.chipsLabel : conjunctionName}`;

          let groupConditionsLabel = '';
          const groupConditions = group.conditions.filter(condition => condition.text);
          groupConditions.filter(condition => condition.text)?.forEach((condition, conditionIndex) => {
            const operatorName = TRANSCRIPTION_SEARCH_OPERATORS.find((operator) => operator.value === condition.operator)?.name.toLowerCase();
            groupConditionsLabel += `${conditionIndex == 0 ? '' : groupConjunctionName + ' '}${operatorName} '${condition.text}', `;
          });
          groupConditionsLabel = groupConditions.length > 1 ? `(${groupConditionsLabel.slice(0, -2)})` : groupConditionsLabel.slice(0, -2);          
          chipsLabel = `${chipsLabel} ${groupConditionsLabel}`;

          chips.push({
            id: index.toString(),
            property: FILTER_DIALOG_OPTIONS.transcriptSearch.name as keyof IFilter,
            label: chipsLabel,
            tooltipText: isFirstChipsItem ? _.upperFirst(groupConditionsLabel) : `${conjunctionName} ${groupConditionsLabel}`,
            showTooltip: false,
          });
        }
      }
    });

    return chips;
  },

  getChipsForScorecardFilter(value: any, scorecards?: ISelectOption<string>[]) : IFilterChips | undefined {
    const filter = value as IScorecardFilter;
    if (!filter?.options?.length) {
      return undefined;
    }

    const selectedConditions: IScorecardFilterCondition[] = filter.options.map(option => option.value);
    const validConditions = selectedConditions.filter(condition => condition.scorecardId);
    if (!validConditions.length) {
      return undefined;
    }

    const resolveName = (scorecardId?: string) =>
      scorecards?.find(s => s.value === scorecardId)?.name ?? scorecardId;
    const formatResult = (condition: IScorecardFilterCondition): string | undefined => {
      const { result, scoreValue } = condition;
      switch (result) {
        case ScorecardFilterConditionResults.isPass: return 'Is Pass';
        case ScorecardFilterConditionResults.isFail: return 'Is Fail';
        case ScorecardFilterConditionResults.isAutoFailed: return 'Autofail';
        case ScorecardFilterConditionResults.isNotApplicable: return 'Not Applicable';
        case ScorecardFilterConditionResults.isError: return 'Is Error';
        case ScorecardFilterConditionResults.isGreaterThan: return `> ${scoreValue}%`;
        case ScorecardFilterConditionResults.isEqualTo: return `= ${scoreValue}%`;
        case ScorecardFilterConditionResults.isLessThan: return `< ${scoreValue}%`;
        default: return undefined;
      }
    };
    const tooltipText = validConditions
      .map(condition => {
        const formattedResult = formatResult(condition);
        return formattedResult
          ? `${resolveName(condition.scorecardId)}: ${formattedResult}`
          : `${resolveName(condition.scorecardId)}`;
      })
      .join(', ');

    const labelSuffix = validConditions.length === 1 
      ? resolveName(validConditions[0].scorecardId)
      : `${validConditions.length} selected`;

    return {
      property: FILTER_DIALOG_OPTIONS.scorecardFilter.name as keyof IFilter,
      label: `${FILTER_DIALOG_OPTIONS.scorecardFilter.chipsLabel}: ${labelSuffix}`,
      tooltipText,
      showTooltip: true,
    };
  },

  getChipsForContactInfoFilter(property: keyof IFilter, value: any) : IFilterChips | undefined {
    const selectedValue = value as string;
    if (!selectedValue) {
      return undefined;
    }

    const optionValue = `contains: '${selectedValue}'`;
    return {
      property: property as keyof IFilter,
      label: `${FILTER_DIALOG_OPTIONS[property].chipsLabel} ${optionValue}`,
      tooltipText: optionValue,
      showTooltip: false,
    };
  },

  getChipsForObjections(value: any, obectionsData: ISelectOption<ObjectionCategory>[]) : IFilterChips | undefined {
    if (Array.isArray(value) && value.length === 0 || !obectionsData) {
      return undefined;
    }

    const selectedValues = value as ITreeItemFilter[];
    const selectedObjectionValues = selectedValues
      .filter(item => !item.selectedAll && item.childItems.length > 0)
      .map(item => item.childItems).flat();

    const selectedAllObjections = obectionsData
      ?.filter(item => selectedValues.filter(selectedItem => selectedItem.selectedAll).some(selected => selected.value === item.value)) ?? [];
      
    const selectedSeparatelyObjections = obectionsData
      ?.filter(item => selectedValues.filter(selectedItem => !selectedItem.selectedAll).some(selectedItem => selectedItem.value === item.value))
      .map((item) => ({
        ...item,
        childItems: item.childItems?.filter(childItem => selectedObjectionValues.some(selected => selected === childItem.value)),
      })) ?? [];

    const selectedObjections = _.concat(selectedAllObjections, selectedSeparatelyObjections);
    selectedObjections.forEach(item => item.childItems = _.sortBy(item.childItems, [childItem => childItem?.name]));
    if (selectedObjections.length === 0) {
      return undefined;
    }

    return {
      property: FILTER_DIALOG_OPTIONS.objections.name as keyof IFilter,
      label: `${FILTER_DIALOG_OPTIONS.objections.chipsLabel}: 
        ${selectedValues.map(item => `${item.value as ObjectionCategory} (${item.selectedAll ? 'All' : item.childItems.length})`).join(', ')}`,
      tooltipText: selectedValues.map(item => `${item.value as ObjectionCategory} 
        (${selectedObjections.find(selectedItem => item.value === selectedItem.value)?.childItems?.map(childItem => childItem.name).join(', ')})`).join(', '),
      showTooltip: true,
    };
  },

  getChipsForDropdown(property: keyof IFilter, value: any, dropdownOptions: ISelectOption<any>[]) : IFilterChips | undefined {
    if (Array.isArray(value) && value.length === 0 || !dropdownOptions) {
      return undefined;
    }

    const selectedValue = value as any[];
    const selectedItems = dropdownOptions.filter(dataItem => selectedValue.includes(dataItem.value));
    if (!selectedItems || selectedItems.length === 0) {
      return undefined;
    }

    return {
      property: property as keyof IFilter,
      label: `${FILTER_DIALOG_OPTIONS[property].chipsLabel}: ${selectedItems?.length === 1 ? selectedItems[0].name : selectedItems.length.toString() + ' selected'}`,
      tooltipText: selectedItems.map(item => item.name).join(', '),
      showTooltip: selectedItems.length > 1
    };
  },

  getChipsForFilterProperty(
    property: keyof IFilter, 
    value: any, 
    filterData: IFilterViewModel | undefined,
    isObjectionsFeatureEnabled: boolean,
    isScorecardsFeatureEnabled: boolean
  ): IFilterChips[] | undefined {
    
    if ((!value && value !== false) || !filterData) {
      return undefined;
    }
  
    let chipsItem: IFilterChips | undefined = undefined;
    switch (property) {
      case FILTER_DIALOG_OPTIONS.recordingLength.name:
        if (!filterService.isFilterRecordingLengthChanged(value, filterData.minDuration, filterData.maxDuration)) {
          return undefined;
        }
        chipsItem = filterService.getChipsForRecordingLength(value);
        break;
      case FILTER_DIALOG_OPTIONS.isSuccess.name:
        chipsItem = filterService.getChipsForIsSuccess(value);
        break;
      case FILTER_DIALOG_OPTIONS.shortCalls.name:
        chipsItem = filterService.getChipsForShortCalls(value);
        break;
      case FILTER_DIALOG_OPTIONS.isScored.name:
        if (!isScorecardsFeatureEnabled) {
          return undefined;
        }
        chipsItem = filterService.getChipsForIsScored(value);
        break;
      case FILTER_DIALOG_OPTIONS.transcriptSearch.name:
        return filterService.getChipsForTranscriptionSearch(value);
      case FILTER_DIALOG_OPTIONS.scorecardFilter.name:
        if (!isScorecardsFeatureEnabled) {
          return undefined;
        }
        chipsItem = filterService.getChipsForScorecardFilter(value, filterData.scorecards);
        break;
      case FILTER_DIALOG_OPTIONS.firstName.name:
      case FILTER_DIALOG_OPTIONS.lastName.name:
      case FILTER_DIALOG_OPTIONS.phoneNumber.name:
      case FILTER_DIALOG_OPTIONS.postalCode.name:
      case FILTER_DIALOG_OPTIONS.referenceId.name:
        chipsItem = filterService.getChipsForContactInfoFilter(property, value);
        break;
      case FILTER_DIALOG_OPTIONS.objections.name:
        if (!isObjectionsFeatureEnabled) {
          return undefined;
        }
        chipsItem = filterService.getChipsForObjections(value, filterData.objections);
        break;
      default: // filter dropdowns values
        const dataSourcePropertyName = FILTER_DIALOG_OPTIONS[property]?.dataSourceName as keyof IFilterViewModel;
        if (!dataSourcePropertyName) {
          return undefined;
        }
        const filterDataItems = filterData[dataSourcePropertyName] as ISelectOption<any>[];
        chipsItem = filterService.getChipsForDropdown(property, value, filterDataItems);
        break;
    }
  
    if (!chipsItem) {
      return undefined;
    }
  
    return [chipsItem];
  },

  getChipsForFilter(
    filter: IFilter, 
    filterData: IFilterViewModel | undefined, 
    isObjectionsFeatureEnabled: boolean,
    isScorecardsFeatureEnabled: boolean
  ): IFilterChips[] {
    let chips: IFilterChips[] = [];
    if (!filter || !filterData) {
      return chips;
    }

    for (const property in filter) {
      const propertyKey = property as keyof IFilter;
      const propertyValue = filter[propertyKey];
      chips = filterService.updateChipsForFilterProperty(
        chips,
        propertyKey,
        propertyValue,
        filterData,
        isObjectionsFeatureEnabled,
        isScorecardsFeatureEnabled);
    }

    return chips;
  },

  updateChipsForFilterProperty(
    filterChips: IFilterChips[],
    property: keyof IFilter, 
    propertyValue: any, 
    filterData: IFilterViewModel | undefined, 
    isObjectionsFeatureEnabled: boolean,
    isScorecardsFeatureEnabled: boolean
  ): IFilterChips[] {
    if (!filterData) {
      return [];
    }

    const chipsIndex = filterChips.findIndex(item => item.property === property);
    const propertyChips = filterService.getChipsForFilterProperty(property, propertyValue, filterData, isObjectionsFeatureEnabled, isScorecardsFeatureEnabled);

    if (!propertyChips || !propertyChips.length) {
      _.remove(filterChips, chips => chips.property === property);
    }
    else if (chipsIndex < 0) {
      filterChips.push(...propertyChips);
    }
    else {
      _.remove(filterChips, chips => chips.property === property);
      filterChips.splice(chipsIndex, 0, ...propertyChips);
    }

    return filterChips;
  },

  clearFilterProperty(filter: IFilter, eventData: IFilterChipsEventData) : IFilter {
    const { itemId, property } = eventData;

    if (property === FILTER_DIALOG_OPTIONS.transcriptSearch.name) {
      if (filter.transcriptSearch.options.length <= 1) {
        filter.transcriptSearch = _.cloneDeep(DEFAULT_TRANSCRIPTION_SEARCH_VALUE);
      }
      else if (itemId) {
        const itemIdNumber = parseInt(itemId);
        // The first items is never removed, but reset to the default state. 
        // This is because the first element should always be the option according to the popup design.
        if (itemIdNumber === 0 && filter.transcriptSearch.options[itemIdNumber + 1].type === TranscriptSearchFilterOptionTypes.group) {
          filter.transcriptSearch.options[itemIdNumber] = _.cloneDeep(DEFAULT_TRANSCRIPTION_SEARCH_OPTION);
        }
        else {
          filter.transcriptSearch.options.splice(itemIdNumber, 1);
        }
      }
    }
    else if (property === FILTER_DIALOG_OPTIONS.isSuccess.name) {
      filter.isSuccess = undefined;
    }
    else if (property === FILTER_DIALOG_OPTIONS.shortCalls.name) {
      filter.shortCalls = DEFAULT_SHORT_CALLS_OPTION.value;
    }
    else if (property === FILTER_DIALOG_OPTIONS.isScored.name) {
      filter.isScored = undefined;
    }
    else if (property === FILTER_DIALOG_OPTIONS.scorecardFilter.name) {
      filter.scorecardFilter = undefined;
    }
    else {
      (filter as any)[property] = undefined;
    }
  
    return filter;
  },
};